// src/lib/evenements-jour.js — LA JOURNÉE DE L'OPÉRATEUR, QUI NE SE PERD PAS.
//
// ══ POURQUOI CE STORE EXISTE ═══════════════════════════════════════════════
//
// La lecture des annonces avance un CURSEUR (`fbz-evenements-vu`) : chaque
// appel ne rend que ce qui est arrivé depuis le précédent. L'écran, lui, se
// rafraîchit tout seul — au retour de focus, au retour d'onglet, toutes les
// trois minutes. Sans mémoire entre deux lectures, une annonce née à 9 h 02
// disparaît à 9 h 05 sans que personne l'ait vue : le curseur est passé, et
// la nouvelle réponse ne la contient plus.
//
// ⚠️ C'ÉTAIT DÉJÀ VRAI AVANT, ET LE RANGEMENT L'AGGRAVE. Depuis que les
// constats se rangent derrière un bouton, une ligne peut naître et mourir
// sans jamais avoir été à l'écran. « Rangé » ne doit jamais vouloir dire
// « jeté » — c'est la règle que Dimitri répète depuis le début.
//
// D'où cette journée, gardée en session (comme le fil de la conversation,
// src/lib/fil-operateur.js) : les annonces s'y ACCUMULENT, dédupliquées par
// leur clé PUIS par leur phrase (`operateur-constats-groupe.js` — deux clés
// différentes peuvent porter le même constat), et n'en sortent que par le
// temps (48 h) ou par minuit.
//
// ══ CE QUE LE STORE SAIT, ET CE QU'IL NE SAIT PAS ══════════════════════════
//
// Il garde des FAITS et trois marques : `vu_le` (quand la ligne est apparue
// ici), `range` (elle a quitté le fil pour le bouton), `traite` (Dimitri l'a
// écartée ou a agi). Il ne sait PAS ce qui presse : le poids se calcule au
// rendu, avec le jour de celui qui regarde (`lib/evenements-poids.js`). Deux
// jugements dans deux endroits, c'est un jugement qui dérive.

import { grouperLesConstats } from "./operateur-constats-groupe";

const CLE = "fbz-evenements-jour";
/** Deux jours : au-delà, une annonce n'est plus la journée de personne. */
const DUREE_MS = 48 * 3600 * 1000;
/** Une borne dure, pour qu'une session très longue ne gonfle pas sans fin. */
const PLAFOND = 300;

let memoire = null;
const abonnes = new Set();

function _lireStockage() {
  try {
    const brut = window.sessionStorage.getItem(CLE);
    const v = brut ? JSON.parse(brut) : null;
    return Array.isArray(v) ? v.filter(Boolean) : [];
  } catch (e) {
    // Navigation privée, stockage refusé : la journée vit en mémoire, et
    // elle meurt avec l'onglet. C'est moins bien, ce n'est pas muet.
    return [];
  }
}

function _ecrire(liste) {
  memoire = liste;
  try {
    window.sessionStorage.setItem(CLE, JSON.stringify(liste));
  } catch (e) {
    /* voir ci-dessus : la mémoire fait foi de toute façon */
  }
  for (const f of abonnes) {
    try {
      f(liste);
    } catch (err) {
      console.error("[evenements-jour] abonné", err);
    }
  }
}

/** La journée telle qu'elle est, la plus ancienne d'abord. */
export function lireLaJournee() {
  if (memoire === null) memoire = _lireStockage();
  return memoire;
}

export function suivreLaJournee(f) {
  abonnes.add(f);
  f(lireLaJournee());
  return () => abonnes.delete(f);
}

/** Le jour d'un horodatage, en clair — sert à faire vieillir la journée. */
function _jourDe(ms) {
  return new Date(ms).toISOString().slice(0, 10);
}

/**
 * Absorbe ce que la lecture vient de rendre.
 *
 * ⚠️ DÉDUP PAR CLÉ, ET LES MARQUES SURVIVENT. Une annonce ré-annoncée (les
 * « états » le sont à chaque lecture tant qu'ils sont vrais) ne revient pas
 * se faire écarter une deuxième fois : son texte se rafraîchit, ses marques
 * restent. Sans ça, écarter un ménage sans agent ne durerait que trois
 * minutes.
 *
 * @param {Array<object>} liste
 * @param {{maintenant?:number}} [opts]
 * @returns {Array<object>} la journée à jour
 */
export function absorberLesEvenements(liste, opts = {}) {
  const maintenant = Number.isFinite(opts.maintenant)
    ? opts.maintenant
    : Date.now();
  const actuelle = lireLaJournee();
  const parCle = new Map(actuelle.map((e) => [e.cle, e]));
  for (const brut of liste || []) {
    if (!brut || !brut.cle) continue;
    const deja = parCle.get(brut.cle);
    parCle.set(brut.cle, {
      ...brut,
      vu_le: deja ? deja.vu_le : maintenant,
      jour: deja ? deja.jour : _jourDe(maintenant),
      range: deja ? !!deja.range : false,
      traite: deja ? !!deja.traite : false,
      // Le compte des lectures survit au rafraîchissement d'une même clé :
      // sans lui, un constat lu trois fois redescendrait à « une fois » au
      // premier renvoi du serveur.
      lectures: deja ? deja.lectures : brut.lectures,
    });
  }
  // ⚠️ ET LE MÊME CONSTAT NE S'INSTALLE PAS DEUX FOIS (Dimitri, 2026-09-16 :
  // « une phrase identique sur la même nature est le MÊME constat. On ne le
  // recopie pas. »). La dédup par clé ci-dessus ne pouvait pas le voir : le
  // doublon vient justement de DEUX clés — une clé embarque souvent un
  // morceau que la phrase ne dit pas (le mois du jour, le dernier
  // identifiant d'un lot, un homonyme). La règle vit dans
  // `src/lib/operateur-constats-groupe.js` (miroir de `lib/`), et le serveur
  // l'applique déjà sur CE QU'IL VIENT DE LIRE — mais lui ne voit qu'une
  // lecture à la fois, alors qu'ici la journée s'accumule sur 48 h.
  //
  // ⚠️ ELLE S'APPLIQUE À L'ENTRÉE, PAS AU RENDU. Le premier arrivé survit
  // avec ses marques (`range`, `traite`, `vu_le`) : ranger un constat ne le
  // fait donc pas revenir sous une autre clé — le second n'entre jamais.
  const gardees = grouperLesConstats([...parCle.values()])
    .filter((e) => maintenant - (e.vu_le || 0) < DUREE_MS)
    .sort((a, b) => (a.vu_le || 0) - (b.vu_le || 0));
  _ecrire(
    gardees.length > PLAFOND
      ? gardees.slice(gardees.length - PLAFOND)
      : gardees,
  );
  return memoire;
}

function _marquer(cle, champs) {
  const liste = lireLaJournee();
  let touche = false;
  const suite = liste.map((e) => {
    if (e.cle !== cle) return e;
    touche = true;
    return { ...e, ...champs };
  });
  if (touche) _ecrire(suite);
  return memoire;
}

/**
 * « Traiter » ne supprime pas : la ligne sort du fil et reste dans la
 * journée (maquette `evTraite`). C'est ce qui permet de la rouvrir, et c'est
 * ce qui fait qu'on ne perd jamais rien.
 */
export function traiterEvenement(cle) {
  return _marquer(cle, { traite: true });
}
export function rouvrirEvenement(cle) {
  return _marquer(cle, { traite: false });
}
/** Ranger : la ligne quitte le fil pour le bouton « il remarque ». */
export function rangerEvenement(cle) {
  return _marquer(cle, { range: true });
}

/**
 * MINUIT : ce qui est traité disparaît, le reste vieillit d'un jour
 * (maquette `jourSuivant`). Appelé au rendu — l'écran qu'on laisse ouvert la
 * nuit doit voir sa journée se refermer sans qu'on le recharge.
 */
export function refermerLaJournee(jour) {
  const liste = lireLaJournee();
  const suite = liste.filter((e) => !(e.traite && e.jour !== jour));
  if (suite.length !== liste.length) _ecrire(suite);
  return memoire;
}

/** L'âge d'une ligne, en jours entiers — « hier », « il y a 2 jours ». */
export function ageEnJours(e, jour) {
  if (!e || !e.jour || !jour) return 0;
  const a = Date.parse(`${e.jour}T00:00:00Z`);
  const b = Date.parse(`${jour}T00:00:00Z`);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  return Math.max(0, Math.round((b - a) / 86400000));
}

/** « 09:12 », l'heure de la ligne dans le fuseau de celui qui regarde. */
export function heureDe(e) {
  const v = e && typeof e.quand === "string" ? e.quand : "";
  if (!v || v.length <= 10) return "";
  const t = Date.parse(v);
  if (!Number.isFinite(t)) return "";
  return new Date(t).toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ══ LES TROIS VUES (maquette : `evJour`, `evRanges`, `evTraites`) ══════════
//
// ⚠️ ELLES SE DÉDUISENT DE LA MÊME LISTE, et c'est ce qui garantit qu'un
// compteur et sa liste disent la même chose. `presse` est une FONCTION passée
// par l'appelant (le poids se calcule au jour de l'écran), jamais une colonne
// figée dans le store.

/** Le fil : ce qui n'est ni traité ni rangé. */
export function vueDuFil(liste) {
  return (liste || []).filter((e) => e && !e.traite && !e.range);
}

/** Le bouton : ce qui est rangé, plus ce qui a été traité sans presser. */
export function vueRangee(liste, presse) {
  return (liste || []).filter(
    (e) => e && (e.range || (e.traite && !presse(e))),
  );
}

/** « Votre journée » : tout, y compris ce qui a été traité. */
export function vueTraitee(liste) {
  return (liste || []).filter((e) => e && e.traite);
}

/** Pour les tests : une journée neuve, sans rien qui traîne. */
export function _reinitialiser() {
  memoire = [];
  try {
    window.sessionStorage.removeItem(CLE);
  } catch (e) {
    /* rien à nettoyer */
  }
}
